import { Stack, Typography, Grid, Box } from "@mui/material";
import theme, { blueGradientSxUp } from "@/theme/theme";
import QrCard from "./qrCard";

const NIST_STANDARDS = [
    {
        name: "ML-KEM",
        standard: "FIPS 203",
        description: "Module-lattice key encapsulation. The post-quantum replacement for the key exchange that protects data in transit.",
    },
    {
        name: "ML-DSA",
        standard: "FIPS 204",
        description: "Module-lattice digital signatures. Quantum-safe authenticity for certificates, software signing and identities.",
    },
    {
        name: "SLH-DSA",
        standard: "FIPS 205",
        description: "Stateless hash-based signatures. A conservative fallback built on well-understood hash function security.",
    },
];

const EU_TIMELINE = [
    {
        year: "2026",
        tag: "First steps",
        label: "National PQC transition roadmaps established, pilots for high- and medium-risk use cases initiated.",
    },
    {
        year: "2030",
        tag: "High-risk done",
        label: "PQC transition for high-risk use cases completed. Quantum-safe software and firmware upgrades enabled by default.",
    },
    {
        year: "2035",
        tag: "Full transition",
        label: "PQC transition for medium-risk use cases completed, low-risk as much as feasible.",
    },
];

const QrUrgency = () => {
    return (
        <Stack
            width={{ xs: "100%", lg: "80%" }}
            marginX="auto"
            paddingX={4}
            gap={6}
            position="relative"
        >
            <Stack gap={2} width={{ xs: "100%", lg: "70%" }}>
                <Typography variant="h3" component="h3" className="chno" fontWeight={400} textTransform="uppercase" fontSize={{ xs: "1.3rem !important", lg: "1.75rem !important" }}>
                    Store now, decrypt later
                </Typography>
                <Typography variant="body2" component="p" fontSize="0.95rem !important" color={theme.palette.text.secondary} fontWeight={400}>
                    The quantum threat is not a future problem. Encrypted traffic intercepted today can be
                    stored cheaply and decrypted the moment a cryptographically relevant quantum computer
                    exists. Any data whose confidentiality must outlive the migration is therefore already
                    at risk. Mosca&apos;s theorem makes the deadline concrete:
                </Typography>
            </Stack>
            <QrCard
                sx={{
                    width: { xs: "100%", md: "fit-content" },
                    marginX: "auto",
                    // Extra breathing room above/below makes this the anchor moment.
                    my: { xs: 1, lg: 2 },
                    height: "auto",
                    // Accent the key insight: brand-tinted glow lifts Mosca's
                    // inequality out of the plain-card rhythm as the section's anchor.
                    backgroundColor: "#0E1730",
                    boxShadow: `0 0 40px -8px ${theme.palette.brand.mgsBlue3.main}55`,
                    "&:hover": {
                        backgroundColor: "#111d3a",
                        transform: "translateY(-4px)",
                        boxShadow: `0 0 56px -6px ${theme.palette.brand.mgsBlue3.main}77`,
                    },
                }}
            >
                <Stack paddingX={{ xs: 2, lg: 6 }} paddingY={{ xs: 2, lg: 3 }} gap={1.5}>
                    <Typography variant="h6" component="p" className="chno" textAlign="center" fontWeight={500} fontSize={{ xs: "1rem !important", lg: "1.5rem !important" }} color={theme.palette.brand.mgsLightBlue1.main}>
                        migration time + confidentiality term &gt; time to quantum computer
                    </Typography>
                    <Typography variant="body2" component="p" textAlign="center" color={theme.palette.text.secondary} fontSize="0.85rem !important">
                        If this holds for your data, and for long-lived data it usually does, the clock is already running.
                    </Typography>
                </Stack>
            </QrCard>
            <Stack gap={3}>
                <Typography variant="h6" component="h6" fontWeight={400}>
                    The standards are here
                </Typography>
                <Grid container spacing={3}>
                    {NIST_STANDARDS.map((std) => (
                        <Grid key={std.name} size={{ xs: 12, md: 4 }}>
                            <QrCard>
                                <Stack gap={1}>
                                    <Stack direction="row" alignItems="baseline" gap={1}>
                                        <Typography variant="h6" component="h6" className="chno" fontWeight={400}>
                                            {std.name}
                                        </Typography>
                                        <Typography variant="body2" component="span" color={theme.palette.brand.mgsLightBlue1.main} fontSize="0.75rem !important">
                                            {std.standard}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" component="p" fontSize="0.85rem !important" color={theme.palette.text.secondary} fontWeight={400}>
                                        {std.description}
                                    </Typography>
                                </Stack>
                            </QrCard>
                        </Grid>
                    ))}
                </Grid>
                <Typography variant="body2" component="p" fontSize="0.85rem !important" color={theme.palette.text.secondary} fontWeight={400}>
                    NIST finalized these standards in 2024, and the practical path is available today: TLS 1.3
                    with hybrid ML-KEM key exchange. There is no TLS 1.4 coming to solve this for you.
                    Regulation is moving too, with the EU coordinated PQC roadmap and NIS2 putting
                    crypto governance on the organisation&apos;s agenda.
                </Typography>
            </Stack>
            <Stack gap={3}>
                <Typography variant="h6" component="h6" fontWeight={400}>
                    The EU coordinated roadmap
                </Typography>
                <Grid container spacing={3}>
                    {EU_TIMELINE.map((step) => (
                        <Grid key={step.year} size={{ xs: 12, md: 4 }}>
                            <QrCard>
                                <Stack gap={1.5} height="100%">
                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                        <Typography component="span" className="chno" fontWeight={600} fontSize={{ xs: "2.4rem !important", lg: "3rem !important" }} sx={blueGradientSxUp(theme)}>
                                            {step.year}
                                        </Typography>
                                        <Box sx={{
                                            border: `1px solid ${theme.palette.brand.mgsLightBlue1.main}`,
                                            borderRadius: 999,
                                            paddingX: 1.5,
                                            paddingY: 0.25,
                                            height: "fit-content",
                                        }}>
                                            <Typography component="span" fontSize="0.7rem !important" color={theme.palette.brand.mgsLightBlue1.main} textTransform="uppercase" letterSpacing={1}>
                                                {step.tag}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <Box sx={{ height: "1px", width: "100%", backgroundColor: theme.palette.brand.border1.main }} />
                                    <Typography variant="body2" component="p" fontSize="0.85rem !important" color={theme.palette.text.secondary} fontWeight={400}>
                                        {step.label}
                                    </Typography>
                                </Stack>
                            </QrCard>
                        </Grid>
                    ))}
                </Grid>
            </Stack>
        </Stack>
    );
};

export default QrUrgency;
